import React from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";

import { selectTheme } from "../../../themes";
import * as Styled from "./NormalInput.styled";

interface ITextInputProps {
  fieldName: string;
  placeholder: string;
  fieldLabel?: string;
  fieldType?: string;
  fieldValue?: string;
  inputLimit?: number;
  onBlurHandler?: (e: Event) => void;
  hasError?: boolean;
  onChangeHandler?: (event: React.ChangeEvent) => void;
  rows?: number;
}

interface IRadioProps {
  fieldName: string;
  fieldLabel: string;
  fieldValue: string | number;
  fieldId: string;
  checked?: boolean;
  description?: string;
  onChangeHandler: (event: React.ChangeEvent) => void;
}

export interface ISelectOption {
  id: string;
  value: string;
  label: string;
  name?: string;
}

interface ISelectProps
  extends Omit<ITextInputProps, "onBlurHandler" | "onChangeHandler" | "inputLimit"> {
  options: ISelectOption[];
  onValueChange?: (value: string) => void;
  onFieldBlur?: () => void;
  menuHeight?: number;
}

export const TextInput: React.FC<ITextInputProps> = ({
  fieldName,
  placeholder,
  fieldLabel,
  fieldType = "text",
  fieldValue,
  inputLimit,
  onChangeHandler,
  onBlurHandler,
  hasError,
}) => {
  return (
    <Styled.FormFieldGroup controlId={fieldName}>
      {fieldLabel && <Styled.FormGroupLabel>{fieldLabel}</Styled.FormGroupLabel>}
      <Styled.FormGroupInput
        value={fieldValue}
        type={fieldType}
        name={fieldName}
        placeholder={placeholder}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        className={hasError ? "is-invalid" : ""}
      />
      {inputLimit && (
        <Styled.FormGroupLimit>
          {fieldValue?.length ? fieldValue.length : 0}/{inputLimit}
        </Styled.FormGroupLimit>
      )}
    </Styled.FormFieldGroup>
  );
};

export const TextAreaInput: React.FC<ITextInputProps> = ({
  fieldName,
  placeholder,
  fieldLabel,
  onChangeHandler,
  fieldValue,
  rows = 3,
}) => {
  return (
    <Styled.FormFieldGroup controlId={fieldName}>
      <Styled.FormGroupLabel>{fieldLabel}</Styled.FormGroupLabel>
      <Styled.TextAreaInput
        as="textarea"
        onChange={onChangeHandler}
        rows={rows}
        value={fieldValue}
        placeholder={placeholder}
        name={fieldName}
      />
    </Styled.FormFieldGroup>
  );
};

export const SelectInput: React.FC<ISelectProps> = ({
  fieldName,
  placeholder,
  options,
  fieldValue,
  fieldLabel = "",
  onValueChange,
  onFieldBlur,
  menuHeight,
  hasError,
}) => {
  const styles = selectTheme.singleSelect(hasError);
  const selectedOption = options.find((option) => option.value === fieldValue) || null;

  return (
    <Styled.FormFieldGroup controlId={fieldName}>
      {fieldLabel && <Styled.FormGroupLabel>{fieldLabel}</Styled.FormGroupLabel>}
      <Select
        inputId={fieldName}
        name={fieldName}
        aria-label={fieldLabel || placeholder}
        classNamePrefix="cemboo-select"
        styles={{
          ...styles,
          menuList: (baseStyles: any) => ({
            ...baseStyles,
            maxHeight: menuHeight || 200,
          }),
        }}
        options={options}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        value={selectedOption}
        placeholder={placeholder}
        menuPortalTarget={typeof document === "undefined" ? undefined : document.body}
        menuPosition="fixed"
        onChange={(option) => onValueChange?.(option?.value || "")}
        onBlur={onFieldBlur}
      />
    </Styled.FormFieldGroup>
  );
};

export const RadioInput: React.FC<IRadioProps> = ({
  fieldLabel,
  fieldName,
  fieldValue,
  fieldId,
  checked,
  description,
  onChangeHandler,
}) => {
  return (
    <Styled.RadioInputWrapper>
      <Styled.RadioInput
        type="radio"
        name={fieldName}
        value={fieldValue}
        id={fieldId}
        defaultChecked={checked}
        onChange={(event) => onChangeHandler(event)}
      />
      <Styled.RadioInputLabel htmlFor={fieldId}>
        {fieldLabel}
        {description && <Styled.RadioInputDescription>{description}</Styled.RadioInputDescription>}
      </Styled.RadioInputLabel>
    </Styled.RadioInputWrapper>
  );
};

export const InputSwtich = () => {
  return <Form.Check type="switch" id="custom-switch" />;
};
